(ns common.resource.query.poi
  (:require [cljs-bean.core :refer [->clj]]
            [clojure.string :as str]
            [common.queries :refer [invalidate-queries]]
            [common.queries.http :as qhttp]
            [re-frame.core :as rf]))

;;
;; Helpers
;;
(defn- on-success-handler [{:keys [entity-name action invalidate-keys]}]
  (fn [_]
    (doseq [k invalidate-keys]
      (invalidate-queries {:queryKey [k]}))
    (rf/dispatch [:response-handler/handle-response
                  {:entity-name entity-name
                   :type :success
                   :message (str "Successfully " action "d " (str/lower-case entity-name))}])))

(defn- on-error-handler [{:keys [entity-name action]}]
  (fn [^js response]
    (rf/dispatch [:response-handler/handle-response
                  {:entity-name entity-name
                   :type :error
                   :message (str "Failed to " action " " (str/lower-case entity-name))
                   :response (->clj response)}])))

(defn- mutation [{:keys [mutation-fn action entity-name] :as params}]
  #js {:mutationKey #js [(str action "-poi-" (-> entity-name str/lower-case))]
       :mutationFn mutation-fn
       :onSuccess (on-success-handler params)
       :onError (on-error-handler params)})

(defn selector-label [{:keys [id external-id name]}]
  (or external-id name (str "Selector " id)))

;;
;; Dataset
;;
(defn ensure-dataset [{:keys [advertiser-id]}]
  #js {:queryKey #js ["poi-dataset" advertiser-id]
       :queryFn (fn [_ctx]
                  (qhttp/put (qhttp/render :api/poi-dataset {:advertiser-id advertiser-id})))
       :enabled (some? advertiser-id)
       :staleTime js/Infinity})

;;
;; Labels
;;
(defn label-options-query [{:keys [advertiser-id enabled?]
                            :or {enabled? true}}]
  #js {:queryKey #js ["poi-label-options" advertiser-id]
       :queryFn (fn [_ctx]
                  (qhttp/get (qhttp/render :api/poi-labels {:advertiser-id advertiser-id})))
       :select (fn [data] (mapv :name (:items (->clj data))))
       :enabled (and (some? advertiser-id) enabled?)})

(defn labels-query [{:keys [advertiser-id enabled?]
                     :or {enabled? true}}]
  #js {:queryKey #js ["poi-labels" advertiser-id]
       :queryFn (fn [_ctx]
                  (qhttp/get (qhttp/render :api/poi-labels {:advertiser-id advertiser-id})))
       :select (fn [data]
                 (->> (:items (->clj data))
                      (sort-by :name)))
       :enabled (and (some? advertiser-id) enabled?)})

(defn create-label-mutation [{:keys [advertiser-id]}]
  (mutation {:entity-name "Label"
             :action "create"
             :invalidate-keys ["poi-labels"]
             :mutation-fn (fn [payload]
                            (qhttp/post (qhttp/render :api/poi-labels {:advertiser-id advertiser-id})
                                        {:body payload}))}))

(defn delete-label-mutation [{:keys [advertiser-id]}]
  (mutation {:entity-name "Label"
             :action "delete"
             :invalidate-keys ["poi-labels"]
             :mutation-fn (fn [label-name]
                            (qhttp/delete (qhttp/render :api/poi-label {:advertiser-id advertiser-id
                                                                        :label label-name})))}))

;;
;; Locations
;;
(defn locations-query [{:keys [advertiser-id page-after page-before per-page q labels
                               all-labels any-labels not-all-labels not-any-labels
                               order-by sort-dir enabled?]
                        :or {per-page 25 enabled? true}}]
  #js {:queryKey #js ["poi-locations" advertiser-id page-after page-before per-page q labels
                      all-labels any-labels not-all-labels not-any-labels order-by sort-dir]
       :queryFn (fn [_ctx]
                  (qhttp/get (qhttp/render :api/poi-locations
                                           {:advertiser-id advertiser-id}
                                           (cond-> {:per-page per-page}
                                             page-after (assoc :page-after page-after)
                                             page-before (assoc :page-before page-before)
                                             (seq q) (assoc :q q)
                                             (seq labels) (assoc :all-labels labels)
                                             (seq all-labels) (assoc :all-labels (vec all-labels))
                                             (seq any-labels) (assoc :any-labels (vec any-labels))
                                             (seq not-all-labels) (assoc :not-all-labels (vec not-all-labels))
                                             (seq not-any-labels) (assoc :not-any-labels (vec not-any-labels))
                                             (some? order-by) (assoc :order-by order-by)
                                             (some? sort-dir) (assoc :sort sort-dir)))))
       :select (fn [data]
                 (let [d (->clj data)]
                   {:items (:items d)
                    :total (:total d)
                    :per-page (:per-page d)
                    :next-cursor (:next-cursor d)
                    :prev-cursor (:prev-cursor d)}))
       :enabled (and (some? advertiser-id) enabled?)
       :keepPreviousData true})

(defn create-location-mutation [{:keys [advertiser-id]}]
  (mutation {:entity-name "Location"
             :action "create"
             :invalidate-keys ["poi-locations" "poi-label-options"]
             :mutation-fn (fn [payload]
                            (qhttp/post (qhttp/render :api/poi-locations {:advertiser-id advertiser-id})
                                        {:body payload}))}))

(defn update-location-mutation [{:keys [advertiser-id]}]
  (mutation {:entity-name "Location"
             :action "update"
             :invalidate-keys ["poi-locations" "poi-label-options"]
             :mutation-fn (fn [^js params]
                            (let [{:keys [location-id payload]} (->clj params)]
                              (qhttp/patch (qhttp/render :api/poi-location {:advertiser-id advertiser-id
                                                                            :location-id location-id})
                                           {:body payload})))}))

(defn delete-location-mutation [{:keys [advertiser-id]}]
  (mutation {:entity-name "Location"
             :action "delete"
             :invalidate-keys ["poi-locations"]
             :mutation-fn (fn [location-id]
                            (qhttp/delete (qhttp/render :api/poi-location {:advertiser-id advertiser-id
                                                                           :location-id location-id})))}))

(defn bulk-replace-locations-mutation [{:keys [advertiser-id]}]
  (mutation {:entity-name "Locations"
             :action "bulk-replace"
             :invalidate-keys ["poi-locations" "poi-label-options"]
             :mutation-fn (fn [form-data]
                            (qhttp/put (qhttp/render :api/poi-locations {:advertiser-id advertiser-id})
                                       {:body form-data
                                        :format :form-data}))}))

(defn bulk-delete-locations-mutation [{:keys [advertiser-id]}]
  (mutation {:entity-name "Locations"
             :action "bulk-delete"
             :invalidate-keys ["poi-locations"]
             :mutation-fn (fn [_]
                            (qhttp/delete (qhttp/render :api/poi-locations {:advertiser-id advertiser-id})))}))

;;
;; Selectors
;;
(defn selectors-query [{:keys [advertiser-id page-after page-before per-page label enabled?]
                        :or {per-page 25 enabled? true}}]
  #js {:queryKey #js ["poi-selectors" advertiser-id page-after page-before per-page label]
       :queryFn (fn [_ctx]
                  (qhttp/get (qhttp/render :api/poi-selectors
                                           {:advertiser-id advertiser-id}
                                           (cond-> {:per-page per-page}
                                             page-after (assoc :page-after page-after)
                                             page-before (assoc :page-before page-before)
                                             (seq label) (assoc :label label)))))
       :select (fn [data]
                 (let [d (->clj data)]
                   {:items (:items d)
                    :total (:total d)
                    :per-page (:per-page d)
                    :next-cursor (:next-cursor d)
                    :prev-cursor (:prev-cursor d)}))
       :enabled (and (some? advertiser-id) enabled?)
       :keepPreviousData true})

(defn selector-options-query [{:keys [advertiser-id]}]
  #js {:queryKey #js ["poi-selectors" advertiser-id nil nil 100 nil]
       :queryFn (fn [_ctx]
                  (qhttp/get (qhttp/render :api/poi-selectors
                                           {:advertiser-id advertiser-id}
                                           {:per-page 100})))
       :select (fn [data]
                 (->> (:items (->clj data))
                      (mapv (fn [selector]
                              {:label (selector-label selector)
                               :value (:id selector)}))
                      (sort-by (comp str/lower-case :label))))
       :enabled (some? advertiser-id)})

(defn selector-query [{:keys [advertiser-id selector-id]}]
  #js {:queryKey #js ["poi-selector" advertiser-id selector-id]
       :queryFn (fn [_ctx]
                  (qhttp/get (qhttp/render :api/poi-selector
                                           {:advertiser-id advertiser-id
                                            :selector-id selector-id})))
       :select (fn [data] (->clj data))
       :enabled (and (some? advertiser-id) (some? selector-id))})

(defn create-selector-mutation [{:keys [advertiser-id]}]
  (mutation {:entity-name "Selector"
             :action "create"
             :invalidate-keys []
             :mutation-fn (fn [payload]
                            (qhttp/post (qhttp/render :api/poi-selectors {:advertiser-id advertiser-id})
                                        {:body payload}))}))

(defn update-selector-mutation [{:keys [advertiser-id]}]
  (mutation {:entity-name "Selector"
             :action "update"
             :invalidate-keys []
             :mutation-fn (fn [^js params]
                            (let [{:keys [selector-id payload]} (->clj params)]
                              (qhttp/patch (qhttp/render :api/poi-selector {:advertiser-id advertiser-id
                                                                            :selector-id selector-id})
                                           {:body payload})))}))

(defn delete-selector-mutation [{:keys [advertiser-id]}]
  (mutation {:entity-name "Selector"
             :action "delete"
             :invalidate-keys ["poi-selectors"]
             :mutation-fn (fn [selector-id]
                            (qhttp/delete (qhttp/render :api/poi-selector {:advertiser-id advertiser-id
                                                                           :selector-id selector-id})))}))
